import React, { useState } from 'react'
import { FormattedTeamSession } from '../../../metrics_server/sessions/types'
import { Button } from '../../Button/Button'
import Checkbox from '../Inputs/Checkbox/Checkbox'
import TextInput from '../Inputs/TextInput/TextInput'
import styles from './styles.module.scss'
import { Group } from '../../../metrics_server/types'
import {
  SessionDownloadType,
  useFormattedSession
} from '../../../metrics_server/sessions/hooks'
import BasicSelect from '../../Material/Select'
import { majorTimezones } from '../../../utils/moment'
import { isAmFooty } from '../../../metrics_server/sports/functions'
import { isLocal } from '../../../metrics_server/env'

export interface DownloadFormState {
  isAdmin: boolean
  teams: Group<FormattedTeamSession>
  handleProceed: (
    type: string | string[],
    offset: number,
    team: string,
    officiationEvents: boolean,
    timezone?: string,
    drills?: boolean
  ) => void
  handleClose: () => void
  sessionId: string
}

const DownloadForm = ({
  isAdmin = false,
  teams,
  handleProceed,
  handleClose,
  sessionId
}: DownloadFormState): JSX.Element => {
  const { sport, isTrainingMode } = useFormattedSession(sessionId)

  // Change from single type to array of selected types for multiselect
  const [selectedTypes, setSelectedTypes] = useState<SessionDownloadType[]>([
    'csv'
  ])
  const [xmlTeam, setXMLTeam] = useState<string>('')
  const [csvTeam, setCSVTeam] = useState<string>('')
  const [offset, setOffset] = useState<number>(0)
  const [selectedTimezone, setSelectedTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  )
  const [officiationEvents, setOfficiationEvents] = useState(false)

  const [selectedDownloads, setSelectedDownloads] = useState({
    csv: {
      drills: false
    },
    xml: {
      drills: false
    }
  })

  // Modified to handle multiselect
  const handleCheckboxChange = (
    checked: boolean,
    type: SessionDownloadType
  ) => {
    if (checked) {
      // Add the type if checked
      setSelectedTypes((prev) => [...prev, type])
    } else {
      // Remove the type if unchecked
      setSelectedTypes((prev) => prev.filter((t) => t !== type))
    }
  }

  const handleXMLTeamCheckboxChange = (value, name: string) => {
    let team = name
    if (!value) {
      team = ''
    }
    setXMLTeam(team)
  }

  const handleCSVTeamCheckboxChange = (value, name: string) => {
    let team = name
    if (!value) {
      team = ''
    }
    setCSVTeam(team)
  }

  let team1: FormattedTeamSession | undefined
  let team2: FormattedTeamSession | undefined
  if (teams?.list?.length === 2) {
    team1 = teams?.list[0]
    team2 = teams?.list[1]
  }

  const timeZoneOptions = majorTimezones.map((timezone) => ({
    name: timezone,
    value: timezone
  }))

  const TimezoneSelect = ({
    selectedTimezone,
    timeZoneOptions,
    setSelectedTimezone
  }: {
    selectedTimezone: string
    timeZoneOptions: { name: string; value: string }[]
    setSelectedTimezone: (value: string) => void
  }) => {
    if (!isAmFooty(sport)) {
      return null
    }

    return (
      <BasicSelect
        label={'Timezone'}
        selected={selectedTimezone}
        options={timeZoneOptions}
        size={'small'}
        variant={'standard'}
        onChange={setSelectedTimezone}
        width={'40%'}
      />
    )
  }

  // Helper function to check if a type is selected
  const isTypeSelected = (type: SessionDownloadType) =>
    selectedTypes.includes(type)

  // Determine which team to use when both CSV and XML are selected
  const determineTeam = () => {
    // If both formats are selected, prioritize csvTeam
    if (isTypeSelected('csv') && isTypeSelected('xml')) {
      return csvTeam
    }
    // Otherwise use the appropriate team based on format
    return isTypeSelected('csv') ? csvTeam : xmlTeam
  }

  // Determine which drills setting to use
  const determineDrills = () => {
    if (isTypeSelected('csv') && isTypeSelected('xml')) {
      // If both are selected, use true if either is true
      return selectedDownloads.csv.drills || selectedDownloads.xml.drills
    }
    return isTypeSelected('csv')
      ? selectedDownloads.csv.drills
      : selectedDownloads.xml.drills
  }

  return (
    <div>
      <h4 style={{ marginBottom: '30px' }}>Select file types to download:</h4>
      <div>
        <div className={styles.allSessionEventsContainer}>
          <Checkbox
            onClicked={(value) => handleCheckboxChange(value, 'csv')}
            label={`CSV Format`}
            checked={isTypeSelected('csv')}
          />
          {isTypeSelected('csv') && (
            <>
              {isTrainingMode && (
                <div className={styles.indent}>
                  <Checkbox
                    onClicked={(value) =>
                      setSelectedDownloads((prev) => ({
                        ...prev,
                        csv: {
                          drills: value
                        }
                      }))
                    }
                    label='Drill events'
                    checked={selectedDownloads.csv.drills}
                  />
                </div>
              )}
              <div className={styles.offsetInput}>
                <div style={{ fontSize: '13px' }}>Offset (s):</div>
                <div className='smallSelectContainer'>
                  <TextInput
                    disabled={!isTypeSelected('csv')}
                    small={true}
                    type='number'
                    input={{
                      onChange: (e) => {
                        const num = Number(e.target.value)
                        setOffset(num)
                      },
                      name: 'offset',
                      value: offset
                    }}
                  />
                </div>
              </div>
              <TimezoneSelect
                selectedTimezone={selectedTimezone}
                timeZoneOptions={timeZoneOptions}
                setSelectedTimezone={setSelectedTimezone}
              />
            </>
          )}
        </div>
        {team1 && team2 && isTypeSelected('csv') && (
          <div className={styles.indent}>
            <Checkbox
              onClicked={(value) => handleCSVTeamCheckboxChange(value, '')}
              label={`Both teams as single file`}
              checked={csvTeam === ''}
              disabled={!isTypeSelected('csv')}
            />
            <Checkbox
              onClicked={(value) => handleCSVTeamCheckboxChange(value, 'both')}
              label={`Both teams as separate files`}
              checked={csvTeam === 'both'}
              disabled={!isTypeSelected('csv')}
            />
            <Checkbox
              onClicked={(value) =>
                handleCSVTeamCheckboxChange(value, team1.teamId)
              }
              label={`${team1.team.name}`}
              checked={csvTeam === team1.teamId}
              disabled={!isTypeSelected('csv')}
            />
            {team2 && (
              <Checkbox
                onClicked={(value) =>
                  handleCSVTeamCheckboxChange(value, team2.teamId)
                }
                label={`${team2.team.name}`}
                checked={csvTeam === team2.teamId}
                disabled={!isTypeSelected('csv')}
              />
            )}
            <Checkbox
              onClicked={(value) => setOfficiationEvents(value)}
              label={`Add Officiating Events`}
              checked={officiationEvents}
              disabled={!isTypeSelected('csv')}
            />
          </div>
        )}

        <div className={styles.allSessionEventsContainer}>
          <Checkbox
            onClicked={(value) => handleCheckboxChange(value, 'xml')}
            label={`XML Format`}
            checked={isTypeSelected('xml')}
          />
          {isTypeSelected('xml') && (
            <>
              {isTrainingMode && (
                <div className={styles.indent}>
                  <Checkbox
                    onClicked={(value) =>
                      setSelectedDownloads((prev) => ({
                        ...prev,
                        xml: {
                          drills: value
                        }
                      }))
                    }
                    label='Drill events'
                    checked={selectedDownloads.xml.drills}
                  />
                </div>
              )}
              <div className={styles.offsetInput}>
                <div style={{ fontSize: '13px' }}>Offset (s):</div>
                <div className='smallSelectContainer'>
                  <TextInput
                    disabled={!isTypeSelected('xml')}
                    small={true}
                    type='number'
                    input={{
                      onChange: (e) => {
                        const num = Number(e.target.value)
                        setOffset(num)
                      },
                      name: 'offset',
                      value: offset
                    }}
                  />
                </div>
              </div>
              <TimezoneSelect
                selectedTimezone={selectedTimezone}
                timeZoneOptions={timeZoneOptions}
                setSelectedTimezone={setSelectedTimezone}
              />
            </>
          )}
        </div>

        {team1 && team2 && isTypeSelected('xml') && (
          <div className={styles.indent}>
            <Checkbox
              onClicked={(value) => handleXMLTeamCheckboxChange(value, '')}
              label={`Both teams as single file`}
              checked={xmlTeam === ''}
              disabled={!isTypeSelected('xml')}
            />
            <Checkbox
              onClicked={(value) => handleXMLTeamCheckboxChange(value, 'both')}
              label={`Both teams as separate files`}
              checked={xmlTeam === 'both'}
              disabled={!isTypeSelected('xml')}
            />
            <Checkbox
              onClicked={(value) =>
                handleXMLTeamCheckboxChange(value, team1.teamId)
              }
              label={`${team1.team.name}`}
              checked={xmlTeam === team1.teamId}
              disabled={!isTypeSelected('xml')}
            />
            {team2 && (
              <Checkbox
                onClicked={(value) =>
                  handleXMLTeamCheckboxChange(value, team2.teamId)
                }
                label={`${team2.team.name}`}
                checked={xmlTeam === team2.teamId}
                disabled={!isTypeSelected('xml')}
              />
            )}
            <Checkbox
              onClicked={(value) => setOfficiationEvents(value)}
              label={`Add Officiating Events`}
              checked={officiationEvents}
              disabled={!isTypeSelected('xml')}
            />
          </div>
        )}

        {!isLocal && (
          <>
            <Checkbox
              onClicked={(value) =>
                handleCheckboxChange(value, 'ball_position')
              }
              label={`Ball Position data`}
              checked={isTypeSelected('ball_position')}
            />
            <Checkbox
              onClicked={(value) =>
                handleCheckboxChange(value, 'player_position')
              }
              label={`Player Position data`}
              checked={isTypeSelected('player_position')}
            />
            <Checkbox
              onClicked={(value) =>
                handleCheckboxChange(value, 'player_position_per_event')
              }
              label={`Tactical Player Position data`}
              checked={isTypeSelected('player_position_per_event')}
            />
            <Checkbox
              onClicked={(value) =>
                handleCheckboxChange(value, 'player_breakdown')
              }
              label={`Player Breakdown`}
              checked={isTypeSelected('player_breakdown')}
            />
            <Checkbox
              onClicked={(value) =>
                handleCheckboxChange(value, 'session_players')
              }
              label={`Session Players`}
              checked={isTypeSelected('session_players')}
            />
          </>
        )}

        {isAdmin && !isLocal && (
          <Checkbox
            onClicked={(value) => handleCheckboxChange(value, 'raw_session')}
            label={`Raw Session Data`}
            checked={isTypeSelected('raw_session')}
          />
        )}
        {isAdmin && (
          <Checkbox
            onClicked={(value) => handleCheckboxChange(value, 'pitch')}
            label={`Pitch & anchor data (.json)`}
            checked={isTypeSelected('pitch')}
          />
        )}
      </div>
      <div className={styles.buttonsContainer}>
        <div style={{ margin: '15px' }}>
          <Button
            type='submit'
            className='btn--primary'
            handleClick={() =>
              handleProceed(
                selectedTypes.length > 1 ? selectedTypes : selectedTypes[0],
                offset,
                determineTeam(),
                officiationEvents,
                selectedTimezone,
                determineDrills()
              )
            }
            disabled={selectedTypes.length === 0}
          >
            Proceed
          </Button>
        </div>
        <div style={{ margin: '15px' }}>
          <Button type='submit' handleClick={handleClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}

export default DownloadForm
